import type { AdmissionRequirement } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { UserCollege } from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, Chip, SxProps, Theme, Tooltip } from '@mui/material';

const STRINGS = appStrings.modules.myColleges;
const TAGS = STRINGS.requirementTags;

type Tag = Readonly<{ key: string; label: string; tooltip: string }>;

const requirementTag = (
  key: keyof typeof TAGS.tooltipSubjects,
  name: string,
  requirement: AdmissionRequirement | null,
): Tag | null => {
  if (!requirement || requirement === 'none') return null;

  const label =
    requirement === 'required' ? name : TAGS.qualified(name, TAGS.qualifiers[requirement]);

  return {
    key,
    label,
    tooltip: `${TAGS.tooltipSubjects[key]} ${TAGS.tooltipRequirements[requirement]}`,
  };
};

const tagsFor = (college: UserCollege, long: boolean) =>
  [
    college.test_optional === null
      ? null
      : {
          key: 'test',
          label: college.test_optional ? TAGS.testOptional : TAGS.test,
          tooltip: college.test_optional ? TAGS.testOptionalTooltip : TAGS.testRequiredTooltip,
        },
    requirementTag('essay', TAGS.essay, college.essay_requirement),
    requirementTag('rec', long ? TAGS.letterOfRec : TAGS.rec, college.recommendation_requirement),
  ].filter((tag): tag is Tag => tag !== null);

type RequirementTagsProps = Readonly<{
  id: string;
  college: UserCollege;
  long?: boolean;
}>;

export const RequirementTags = ({ id, college, long = false }: RequirementTagsProps) => (
  <Box id={id} sx={tagsSx}>
    {tagsFor(college, long).map((tag) => (
      <Tooltip key={tag.key} title={tag.tooltip} placement="top" arrow>
        <Chip variant="badge" size="sm" label={tag.label} sx={tagSx} />
      </Tooltip>
    ))}
  </Box>
);

const tagsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-xs'],
});

const tagSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Brand/200'],
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Brand/700'],
});
